/**
 * @fileoverview Haptic feedback hook for touch interactions
 * @module TechStackAnalysis/hooks/useHapticFeedback
 */

import { useCallback } from 'react';

// Vibration patterns (duration in milliseconds)
const HAPTIC_PATTERNS = {
  light: [10],           // Quick tap (vendor click)
  medium: [20],          // Panel open
  success: [10, 50, 10], // Action complete
  error: [50, 30, 50],   // Warning/error
  select: [5],           // Selection change
  heavy: [30]            // Significant action
};

/**
 * Hook providing haptic feedback utilities
 * @returns {Object} Haptic feedback methods
 */
const useHapticFeedback = () => {
  /**
   * Check if vibration is supported
   */
  const isSupported = typeof navigator !== 'undefined' && 'vibrate' in navigator;

  /**
   * Trigger haptic feedback
   * @param {string} pattern - Pattern name: 'light' | 'medium' | 'success' | 'error' | 'select' | 'heavy'
   */
  const trigger = useCallback((pattern = 'light') => {
    if (!isSupported) return;

    const vibrationPattern = HAPTIC_PATTERNS[pattern] || HAPTIC_PATTERNS.light;

    try {
      navigator.vibrate(vibrationPattern);
    } catch (e) {
      // Silently fail if vibration is not allowed
    }
  }, [isSupported]);

  /**
   * Light tap feedback - for minor interactions
   */
  const light = useCallback(() => trigger('light'), [trigger]);

  /**
   * Medium feedback - for opening panels, navigation
   */
  const medium = useCallback(() => trigger('medium'), [trigger]);

  /**
   * Success feedback - for completed actions
   */
  const success = useCallback(() => trigger('success'), [trigger]);

  /**
   * Error feedback - for warnings and errors
   */
  const error = useCallback(() => trigger('error'), [trigger]);

  /**
   * Selection feedback - for quick selection changes
   */
  const select = useCallback(() => trigger('select'), [trigger]);

  /**
   * Heavy feedback - for significant actions
   */
  const heavy = useCallback(() => trigger('heavy'), [trigger]);

  return {
    isSupported,
    trigger,
    light,
    medium,
    success,
    error,
    select,
    heavy
  };
};

export default useHapticFeedback;
